/**
 * Zustand Store - Restaurant Data State Management
 * 
 * Centralized state for restaurant data, eliminating prop drilling.
 * Provides granular selectors for better performance.
 */

import { create } from 'zustand';
import { RestaurantData, MenuCategory, BusinessInfo, CustomerPreferences, Booking, Room } from '@deskbay/shared/types';
import { SubscriptionCheckResult } from '../types/subscription';

/** Evento realtime booking per comunicare dal data-layer (AuthContext) all'UI (BookingManager). */
export type BookingRealtimeEvent = {
    type: 'INSERT' | 'UPDATE' | 'DELETE';
    booking: Booking;
    timestamp: number; // Per ordinare eventi in coda
};

interface RestaurantStore {
    // State
    data: RestaurantData | null;
    isLoading: boolean;
    isSaving: boolean;
    error: string | null;
    subscription: SubscriptionCheckResult | null;
    subscriptionLoading: boolean;

    // Actions - Full data
    setData: (data: RestaurantData | null) => void;
    setSubscription: (subscription: SubscriptionCheckResult | null) => void;
    setSubscriptionLoading: (loading: boolean) => void;
    reset: () => void;

    // Actions - Partial updates
    updateBusinessInfo: (info: Partial<BusinessInfo>) => void;
    updateMenu: (menu: MenuCategory[]) => void;
    updateCustomerPreferences: (prefs: Partial<CustomerPreferences>) => void;
    updateBookings: (bookings: Booking[]) => void;
    updateRooms: (rooms: Room[]) => void;

    // Realtime event bus: AuthContext scrive, BookingManager legge per UI feedback
    lastBookingEvent: BookingRealtimeEvent | null;
    setLastBookingEvent: (event: BookingRealtimeEvent | null) => void;
    
    // CRITICAL FIX: Pending events queue per eventi ricevuti in background
    // Quando l'app è in background, gli eventi vengono accodati qui.
    // Al ritorno in foreground, vengono riprodotti uno alla volta.
    pendingBookingEvents: BookingRealtimeEvent[];
    addPendingBookingEvent: (event: BookingRealtimeEvent) => void;
    consumePendingEvents: () => BookingRealtimeEvent[];
    clearPendingEvents: () => void;

    // Deeplink: pending booking to open after a restaurant switch
    pendingDeeplinkBookingId: string | null;
    setPendingDeeplinkBookingId: (id: string | null) => void;

    // State management
    setLoading: (loading: boolean) => void;
    setSaving: (saving: boolean) => void;
    setError: (error: string | null) => void;
}

export const useRestaurantStore = create<RestaurantStore>((set, get) => ({
    // Initial state
    data: null,
    isLoading: false,
    isSaving: false,
    error: null,
    subscription: null,
    subscriptionLoading: false,
    lastBookingEvent: null,
    pendingBookingEvents: [],
    pendingDeeplinkBookingId: null,

    // Set full data — also reset subscription so it's re-fetched for the new restaurant
    setData: (data) => set((state) => {
        const prevId = state.data?.directoryId;
        const newId = data?.directoryId;
        // Reset subscription when restaurant changes
        if (prevId !== newId) {
            return { data, error: null, subscription: null, subscriptionLoading: false };
        }
        // Same restaurant: preserve existing menu in store if new data arrives with empty menu.
        // This prevents redundant re-fetches on auth refreshes (Realtime reconnect, focus events).
        const existingMenu = state.data?.menu;
        if (data && prevId === newId && existingMenu && existingMenu.length > 0 && (!data.menu || data.menu.length === 0)) {
            return { data: { ...data, menu: existingMenu }, error: null };
        }
        return { data, error: null };
    }),

    setSubscription: (subscription) => set({ subscription }),
    setSubscriptionLoading: (subscriptionLoading) => set({ subscriptionLoading }),

    // Reset store — chiamato al logout e ad ogni cambio di utente (AuthContext).
    // Azzera TUTTO ciò che è legato al ristorante corrente, eventi realtime inclusi:
    // un `lastBookingEvent` o un deeplink rimasto in coda verrebbe altrimenti
    // riprodotto (toast + suono) nella sessione dell'account successivo.
    reset: () => set({
        data: null,
        isLoading: false,
        isSaving: false,
        error: null,
        subscription: null,
        subscriptionLoading: false,
        lastBookingEvent: null,
        pendingBookingEvents: [],
        pendingDeeplinkBookingId: null
    }),

    // Update business info only
    updateBusinessInfo: (info) => set((state) => {
        if (!state.data) return state;
        return {
            data: {
                ...state.data,
                businessInfo: { ...state.data.businessInfo, ...info }
            }
        };
    }),

    // Update menu
    updateMenu: (menu) => set((state) => {
        if (!state.data) return state;
        return {
            data: { ...state.data, menu }
        };
    }),

    // Update customer preferences
    updateCustomerPreferences: (prefs) => set((state) => {
        if (!state.data) return state;
        return {
            data: {
                ...state.data,
                customerPreferences: { ...state.data.customerPreferences, ...prefs }
            }
        };
    }),

    // Update bookings
    updateBookings: (bookings) => set((state) => {
        if (!state.data) return state;
        return {
            data: { ...state.data, bookings }
        };
    }),

    // Update rooms
    updateRooms: (rooms) => set((state) => {
        if (!state.data) return state;
        return {
            data: { ...state.data, rooms }
        };
    }),

    // Realtime event bus
    setLastBookingEvent: (event) => set({ lastBookingEvent: event }),

    // Pending events queue management
    addPendingBookingEvent: (event) => set((state) => ({
        pendingBookingEvents: [...state.pendingBookingEvents, event]
    })),
    
    consumePendingEvents: () => {
        const events = get().pendingBookingEvents;
        set({ pendingBookingEvents: [] });
        return events.sort((a, b) => a.timestamp - b.timestamp);
    },
    
    clearPendingEvents: () => set({ pendingBookingEvents: [] }),

    // Deeplink pending booking
    setPendingDeeplinkBookingId: (id) => set({ pendingDeeplinkBookingId: id }),

    // State flags
    setLoading: (loading) => set({ isLoading: loading }),
    setSaving: (saving) => set({ isSaving: saving }),
    setError: (error) => set({ error }),
}));
